// The session's side pane: Terminal, Changes, Issue and SubAgents, in today's order.
import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { cx } from '../transcript/bits'
import { countText } from './format'
import { CloseIcon } from './icons'
import { useTabScope } from '../workspace/context'

export type PaneTab = 'terminal' | 'changes' | 'issue' | 'subagents'

export const PANE_TABS: readonly { tab: PaneTab; title: string }[] = [
  { tab: 'terminal', title: 'Terminal' },
  { tab: 'changes', title: 'Changes' },
  { tab: 'issue', title: 'Issue' },
  { tab: 'subagents', title: 'SubAgents' },
]

/** The narrowest the pane goes, and how much a press of an arrow moves its edge. */
export const PANE_MIN = 300
const PANE_STEP = 24
/** What the list and the transcript keep beside it, from 900 px wide; narrower, the pane lies over them. */
const BESIDE_PX = 264 + 380
const OVERLAY_BELOW = 900
const OVERLAY_MARGIN = 40

/** The widest the pane goes in a window that wide. */
export function paneMax(viewport: number): number {
  return Math.max(PANE_MIN, viewport - (viewport >= OVERLAY_BELOW ? BESIDE_PX : OVERLAY_MARGIN))
}

export function clampPane(width: number, viewport: number): number {
  return Math.round(Math.min(paneMax(viewport), Math.max(PANE_MIN, width)))
}

/** The Code page's available width, kept current as its workspace pane resizes. */
export function useViewportWidth(): number {
  const scope = useTabScope()
  const [width, setWidth] = useState(() => window.innerWidth)
  useEffect(() => {
    const surface = scope?.surface()
    const onResize = () => {
      const next = surface ? surface.getBoundingClientRect().width : window.innerWidth
      if (next > 0) setWidth(next)
    }
    onResize()
    const observer = surface && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null
    if (surface) observer?.observe(surface)
    window.addEventListener('resize', onResize)
    return () => { observer?.disconnect(); window.removeEventListener('resize', onResize) }
  }, [scope])
  return width
}

export interface RightPaneProps {
  tab: PaneTab
  onTab: (tab: PaneTab) => void
  /** Its width as shown: already within what the window allows. */
  width: number
  viewport: number
  onWidth: (width: number) => void
  onClose: () => void
  /** How many workers the open session delegated. */
  subagents: number
  terminal?: ReactNode
  changes: ReactNode
  issue: ReactNode
  subagentsPane: ReactNode
}

function Resizer({ width, viewport, onWidth }: { width: number; viewport: number; onWidth: (width: number) => void }) {
  const drag = useRef<{ x: number; width: number } | null>(null)
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    drag.current = { x: event.clientX, width }
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const from = drag.current
    // The pane is on the right: dragging its edge to the left widens it.
    if (from) onWidth(clampPane(from.width + (from.x - event.clientX), viewport))
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: number
    if (event.key === 'ArrowLeft') next = width + PANE_STEP
    else if (event.key === 'ArrowRight') next = width - PANE_STEP
    else if (event.key === 'Home') next = paneMax(viewport)
    else if (event.key === 'End') next = PANE_MIN
    else return
    event.preventDefault()
    onWidth(clampPane(next, viewport))
  }
  return (
    <div
      className="code-pane-resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Width of the side pane"
      aria-valuemin={PANE_MIN}
      aria-valuemax={paneMax(viewport)}
      aria-valuenow={width}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onKeyDown={onKeyDown}
    />
  )
}

export function RightPane({ tab, onTab, width, viewport, onWidth, onClose, subagents, terminal, changes, issue, subagentsPane }: RightPaneProps) {
  const entries = terminal === undefined ? PANE_TABS.filter(entry => entry.tab !== 'terminal') : PANE_TABS
  const base = useId()
  const tabs = useRef<HTMLDivElement>(null)

  const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const at = entries.findIndex((entry) => entry.tab === tab)
    let to: number
    if (event.key === 'ArrowRight') to = (at + 1) % entries.length
    else if (event.key === 'ArrowLeft') to = (at - 1 + entries.length) % entries.length
    else if (event.key === 'Home') to = 0
    else if (event.key === 'End') to = entries.length - 1
    else return
    event.preventDefault()
    onTab(entries[to]!.tab)
    tabs.current?.querySelectorAll<HTMLElement>('[role="tab"]')[to]?.focus()
  }

  return (
    <aside className="code-pane" aria-label="Side pane">
      <Resizer width={width} viewport={viewport} onWidth={onWidth} />
      <div className="code-pane-head">
        <div className="code-pane-tabs" role="tablist" aria-label="Side pane" ref={tabs} onKeyDown={onTabKey}>
          {entries.map((entry) => (
            <button
              key={entry.tab}
              type="button"
              role="tab"
              id={`${base}-${entry.tab}-tab`}
              className={cx('code-pane-tab', entry.tab === tab && 'is-current')}
              aria-selected={entry.tab === tab}
              aria-controls={`${base}-${entry.tab}`}
              tabIndex={entry.tab === tab ? 0 : -1}
              title={entry.tab === 'subagents' && subagents > 0 ? countText(subagents, 'subagent') : undefined}
              onClick={() => onTab(entry.tab)}
            >
              {entry.title}
              {/* The tab keeps its name: the count is its description. */}
              {entry.tab === 'subagents' && subagents > 0 && (
                <span className="code-pane-tab-count" aria-hidden="true">
                  {subagents}
                </span>
              )}
            </button>
          ))}
        </div>
        <button type="button" className="button button-quiet code-icon-button" aria-label="Hide the side pane" title="Hide the side pane" onClick={onClose}>
          <CloseIcon size={14} />
        </button>
      </div>
      <div className="code-pane-body">
        <div id={`${base}-${tab}`} role="tabpanel" aria-labelledby={`${base}-${tab}-tab`} className={`code-pane-panel code-pane-${tab}`}>
          {tab === 'terminal' ? terminal : tab === 'changes' ? changes : tab === 'issue' ? issue : subagentsPane}
        </div>
      </div>
    </aside>
  )
}
